import {
  AwardSubCriterionAnswerFormat,
  AwardCriterionType,
  type AwardCriterionDto,
  type AwardSubCriterionDto,
} from "../../autogen/bff-api";
import { effectiveWeight } from "../../pages/sourcing-events/buyer/wizard/utils/awardCriteriaStatus";
import { isEnvironmentCriterionType } from "../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";

/**
 * One row the supplier answers, mirroring the BFF's ScorableCriterion.toScorableRows(): a criterion
 * without sub-criteria is its own row, a criterion with them contributes one row per sub-criterion
 * and is never answered itself. Keeping the two in step is what makes the wizard's "X of Y answered"
 * agree with the delivery preflight and with the buyer's evaluation grid.
 *
 * A criterion has no authored answer format of its own, so its row answers as free text with no
 * documentation demanded — the same default the BFF applies.
 */
export interface ScorableAwardRow {
  id: string;
  /** The criterion the row belongs to; null when the criterion is the row. */
  parentId: string | null;
  /** Heading the row sits under: the criterion's name, falling back to its type. */
  groupLabel: string;
  groupWeightPercent: number | null;
  type: AwardCriterionType;
  name: string | null;
  description: string;
  /** The weight as authored: a share of the total for a criterion row, of the parent for a sub-row. */
  ownWeightPercent: number | null;
  /** The share of the total, which is what the weighted sum uses. */
  effectiveWeightPercent: number | null;
  answerFormat: AwardSubCriterionAnswerFormat;
  requiresDocumentation: boolean;
  /** Price rows carry no answer of their own — they are scored from the tendered price. */
  isPrice: boolean;
}

export const scorableAwardRows = (
  criteria: AwardCriterionDto[] | undefined,
  typeLabel: (type: AwardCriterionType) => string,
): ScorableAwardRow[] =>
  (criteria ?? [])
    // A weightless environment criterion is recorded but never scored — the § 5c exemption marker and
    // an unweighted "handled in the specification" row. The BFF leaves them out of the evaluation grid
    // and out of what the supplier is asked to answer, so this mirror has to leave them out too, or
    // the count it feeds can never be reached.
    .filter((criterion) => !(isEnvironmentCriterionType(criterion.type) && criterion.weight?.number == null))
    .flatMap((criterion) => {
    const groupLabel = criterion.name?.trim() || typeLabel(criterion.type);
    const subCriteria = criterion.subCriteria ?? [];
    if (subCriteria.length === 0) {
      return [
        {
          id: criterion.id ?? "",
          parentId: null,
          groupLabel,
          groupWeightPercent: criterion.weight?.number ?? null,
          type: criterion.type,
          name: criterion.name ?? null,
          description: criterion.description,
          ownWeightPercent: criterion.weight?.number ?? null,
          effectiveWeightPercent: criterion.weight?.number ?? null,
          answerFormat: AwardSubCriterionAnswerFormat.FreeText,
          requiresDocumentation: false,
          isPrice: criterion.type === AwardCriterionType.Price,
        },
      ];
    }
    return subCriteria.map((sub: AwardSubCriterionDto) => ({
      id: sub.id ?? "",
      parentId: criterion.id ?? null,
      groupLabel,
      groupWeightPercent: criterion.weight?.number ?? null,
      // A sub-criterion inherits its criterion's type, so a row under a price criterion is a price row.
      type: criterion.type,
      name: sub.name ?? null,
      description: sub.description,
      ownWeightPercent: sub.weight?.number ?? null,
      effectiveWeightPercent: effectiveWeight(criterion, sub),
      answerFormat: sub.answerFormat ?? AwardSubCriterionAnswerFormat.FreeText,
      requiresDocumentation: sub.requiresDocumentation ?? false,
      isPrice: criterion.type === AwardCriterionType.Price,
    }));
  });

/** The rows grouped for rendering, preserving the buyer's criterion order. */
export interface ScorableAwardGroup {
  key: string;
  label: string;
  type: AwardCriterionType;
  weightPercent: number | null;
  /** A criterion without sub-criteria is its own row, so the group heading would only repeat it. */
  hasOwnHeading: boolean;
  /** The price criterion is scored from the tendered price, never by hand. */
  isPrice: boolean;
  rows: ScorableAwardRow[];
}

/** The rows as groups, one per criterion, in the order the buyer authored them. */
export const groupScorableAwardRows = (rows: ScorableAwardRow[]): ScorableAwardGroup[] => {
  const groups: ScorableAwardGroup[] = [];
  rows.forEach((row) => {
    const key = row.parentId ?? row.id;
    const existing = groups.find((group) => group.key === key);
    if (existing) {
      existing.rows.push(row);
      return;
    }
    groups.push({
      key,
      label: row.groupLabel,
      type: row.type,
      weightPercent: row.groupWeightPercent,
      hasOwnHeading: row.parentId !== null,
      isPrice: row.isPrice,
      rows: [row],
    });
  });
  return groups;
};

/** What a group asks for: one tendered price, however many rows the buyer split its weight into. */
export const answerRowsOf = (group: ScorableAwardGroup): ScorableAwardRow[] =>
  group.isPrice && group.rows.length > 1 ? [priceAnswerRow(group)] : group.rows;

const priceAnswerRow = (group: ScorableAwardGroup): ScorableAwardRow => ({
  ...group.rows[0],
  parentId: null,
  name: group.label,
  description: "",
  ownWeightPercent: group.weightPercent,
  effectiveWeightPercent: group.weightPercent,
  requiresDocumentation: false,
});

/** The rows that split a price criterion's weight, which the supplier reads but never answers. */
export const priceWeightRowsOf = (group: ScorableAwardGroup): ScorableAwardRow[] =>
  group.isPrice && group.rows.length > 1 ? group.rows : [];

/**
 * Price first, everything else as authored. The buyer reads an offer with the price in hand and
 * weighs the rest against it, so the evaluation and the delivered-answer views lead with it. The
 * supplier's answering form does not: there the order is the one the competition documents list.
 * Array.prototype.sort is stable, so the groups below price keep their authored order.
 */
export const priceFirst = (groups: ScorableAwardGroup[]): ScorableAwardGroup[] =>
  [...groups].sort((a, b) => Number(b.isPrice) - Number(a.isPrice));
